// Bridges the original KnowMeno App (which stores its `user` in localStorage via
// window.storage) with the Supabase `profiles` table.
//
// Strategy:
//  - On mount: load the profile row, hydrate localStorage so the original App
//    bootstraps with the right user object (name, life stage, focus area,
//    premium flag, signupDate from trial_started_at, plus the HRT / health
//    fields stored in the `health_profile` JSONB column).
//  - On window storage change: persist relevant fields back to the profiles row.
import { createContext, useContext, useEffect, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { postSignupHandler } from "@/lib/post-signup.functions";
import { captureTimezone } from "@/lib/notifications.functions";
import { claimReferralOnSignup } from "@/lib/referrals.functions";
import { getPendingRef, clearPendingRef } from "@/lib/refParam";
import { PushOptInPrompt } from "./PushOptInPrompt";
import { silentResubscribeIfNeeded } from "./silentResubscribe";
import { pushSubscribe } from "@/lib/push-public.functions";
import { useServerFn } from "@tanstack/react-start";
import { isDevAuthSession } from "@/knowmeno/devAuthBypass";

const USER_KEY = "menotrack_user";

// ─── Plan-tier context ──────────────────────────────────────────────────────
// Mirrors the DB helper `current_plan_tier(user_id)`:
//   is_test_user → 'plus'
//   else         → profiles.plan_tier ?? 'free'
// By construction (see migration backfill), the set of users with isPlus=true
// is identical to the set who previously had isPremium = (is_premium || is_test_user).
export type PlanTier = "free" | "plus" | "premium";
export interface PlanFlags {
  planTier: PlanTier;
  isFree: boolean;
  isPlus: boolean;
  isPremium: boolean;
  isLaunchComp: boolean;
  launchCompGrantedAt: string | null;
  compExpiresAt: string | null;
  isPaidPremium: boolean;
}
const PlanContext = createContext<PlanFlags>({
  planTier: "free",
  isFree: true,
  isPlus: false,
  isPremium: false,
  isLaunchComp: false,
  launchCompGrantedAt: null,
  compExpiresAt: null,
  isPaidPremium: false,
});
export function usePlan(): PlanFlags {
  return useContext(PlanContext);
}
// Mirrors the DB has_active_access launch-comp rule:
//   is_launch_comp grants Plus only when comp_expires_at IS NULL
//   (no expiry scheduled) OR comp_expires_at is in the future.
function derivePlanFlags(row: { is_test_user?: boolean | null; is_launch_comp?: boolean | null; is_premium?: boolean | null; plan_tier?: string | null; launch_comp_granted_at?: string | null; comp_expires_at?: string | null } | null): PlanFlags {
  const compExpiresAt = row?.comp_expires_at ?? null;
  const compActive =
    row?.is_launch_comp === true &&
    (compExpiresAt === null || new Date(compExpiresAt).getTime() > Date.now());
  let planTier: PlanTier = "free";
  if (row?.is_test_user === true || compActive) {
    planTier = "plus";
  } else {
    const raw = (row?.plan_tier ?? "free") as string;
    planTier = raw === "premium" ? "premium" : raw === "plus" ? "plus" : "free";
  }
  return {
    planTier,
    isFree: planTier === "free",
    isPlus: planTier === "plus" || planTier === "premium",
    isPremium: planTier === "premium",
    isLaunchComp: row?.is_launch_comp === true,
    launchCompGrantedAt: row?.launch_comp_granted_at ?? null,
    compExpiresAt,
    isPaidPremium: row?.is_premium === true,
  };
}


interface Props {
  session: Session;
  children: React.ReactNode;
}

type HealthProfile = {
  hrtType?: string;
  hrtTypes?: string[];
  hrtDose?: string;
  hrtDuration?: string;
  hrtSchedule?: string;
  hrtStartDate?: string | null;
  otherMeds?: string;
  dob?: string;
  otherConditions?: string;
  device?: string;
  stage?: string;
};

type ProfileRow = {
  id: string;
  name: string | null;
  email: string | null;
  life_stage: string | null;
  focus_area: string | null;
  onboarding_completed: boolean;
  is_premium: boolean;
  is_test_user?: boolean | null;
  is_launch_comp?: boolean | null;
  plan_tier?: string | null;
  comp_expires_at?: string | null;
  trial_started_at: string | null;
  trial_ends_at: string | null;
  health_profile: HealthProfile | null;
  last_period_date: string | null;
  stage_changed_at: string | null;
  wearable_device: string | null;
};


function splitName(full: string | null | undefined): { firstName: string; lastName: string } {
  const trimmed = (full || "").trim();
  if (!trimmed) return { firstName: "", lastName: "" };
  const parts = trimmed.split(/\s+/);
  return {
    firstName: parts[0] || "",
    lastName: parts.slice(1).join(" "),
  };
}

function appUserFromProfile(p: ProfileRow) {
  const { firstName, lastName } = splitName(p.name);
  const hp = p.health_profile || {};
  return {
    firstName,
    lastName,
    name: p.name || "",
    email: p.email || "",
    lifeStage: p.life_stage || "",
    stage: hp.stage || p.life_stage || "",
    focusArea: p.focus_area || "",
    // Internal test users and launch-comp users bypass all paywall gating client-side.
    isPremium: p.is_premium || derivePlanFlags(p).isPlus,
    isTestUser: p.is_test_user === true,
    healthProfileComplete: p.onboarding_completed,
    signupDate: p.trial_started_at || new Date().toISOString(),
    // Health profile fields
    hrtType: hp.hrtType || "",
    hrtTypes: hp.hrtTypes || [],
    hrtDose: hp.hrtDose || "",
    hrtDuration: hp.hrtDuration || "",
    hrtSchedule: hp.hrtSchedule || "",
    hrtStartDate: hp.hrtStartDate || null,
    otherMeds: hp.otherMeds || "",
    dob: hp.dob || "",
    otherConditions: hp.otherConditions || "",
    device: hp.device || "",
    wearableDevice: p.wearable_device || "",
    lastPeriodDate: p.last_period_date || null,
    stageChangedAt: p.stage_changed_at || null,
  };
}

export function ProfileSync({ session, children }: Props) {
  const [ready, setReady] = useState(false);
  const [planFlags, setPlanFlags] = useState<PlanFlags>(() => derivePlanFlags(null));
  const subscribeFn = useServerFn(pushSubscribe);
  const devBypass = isDevAuthSession(session);

  // Dev-only auth bypass: seed a usable fake user and skip remote profile I/O.
  useEffect(() => {
    if (!devBypass) return;
    try {
      const existing = window.localStorage.getItem(USER_KEY);
      if (!existing) {
        const seeded = {
          firstName: "Dev",
          lastName: "Bypass",
          name: "Dev Bypass",
          email: session.user.email || "dev-bypass@knowmeno.test",
          lifeStage: "Perimenopause",
          stage: "Perimenopause",
          focusArea: "Symptom tracking",
          isPremium: true,
          isTestUser: true,
          healthProfileComplete: true,
          signupDate: new Date().toISOString(),
          hrtType: "",
          hrtTypes: [],
          hrtDose: "",
          hrtDuration: "",
          hrtSchedule: "",
          hrtStartDate: null,
          otherMeds: "",
          dob: "1975-01-01",
          otherConditions: "",
          device: "",
          wearableDevice: "",
          lastPeriodDate: null,
          stageChangedAt: null,
        };
        window.localStorage.setItem(USER_KEY, JSON.stringify(seeded));
      }
    } catch { /* no-op */ }
    setPlanFlags({
      planTier: "plus",
      isFree: false,
      isPlus: true,
      isPremium: false,
      isLaunchComp: false,
      launchCompGrantedAt: null,
      compExpiresAt: null,
      isPaidPremium: false,
    });
    setReady(true);
  }, [devBypass, session.user.email]);

  // Initial hydrate: load profile -> localStorage
  useEffect(() => {
    if (devBypass) return;
    let cancelled = false;

    const loadProfile = async (isInitial: boolean) => {
      const { data, error } = await supabase
        .from("profiles")
        .select("*")
        .eq("id", session.user.id)
        .maybeSingle();
      if (cancelled) return;
      if (error) {
        console.error("Failed to load profile", error);
      }
      if (data) {
        const seeded = appUserFromProfile(data as ProfileRow);
        if (data.onboarding_completed || seeded.firstName) {
          window.localStorage.setItem(USER_KEY, JSON.stringify(seeded));
        } else if (isInitial) {
          window.localStorage.removeItem(USER_KEY);
        }
        setPlanFlags(derivePlanFlags(data as ProfileRow));
        if (isInitial) {
          try {
            const ph = (window as any).posthog;
            if (ph?.identify) {
              ph.identify(session.user.id, {
                email: data.email ?? session.user.email ?? undefined,
                launch_comp: data.is_launch_comp === true,
                launch_comp_granted_at: data.launch_comp_granted_at ?? null,
              });
            }
          } catch {}
        }
      }
      if (isInitial) {
        setReady(true);

        const pendingRef = getPendingRef();
        if (pendingRef) {
          try {
            const res = await claimReferralOnSignup({ data: { code: pendingRef } });
            if (res?.ok || (res as any)?.reason === "already_referred") {
              clearPendingRef();
            }
          } catch (e) {
            console.warn("[referrals] claim failed", e);
          }
        }
      }
    };

    loadProfile(true);

    // Refresh plan flags when the user returns to the tab (debounced).
    let lastRun = 0;
    const refresh = () => {
      const now = Date.now();
      if (now - lastRun < 2000) return;
      lastRun = now;
      loadProfile(false).catch(() => {});
    };
    const onFocus = () => refresh();
    const onVisibility = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [session.user.id]);


  // Capture browser timezone + register the service worker for push.
  // Both are best-effort and silently no-op in iframes / preview hosts.
  useEffect(() => {
    if (!ready) return;
    if (devBypass) return;
    if (typeof window === "undefined") return;

    let inIframe = false;
    try {
      inIframe = window.self !== window.top;
    } catch {
      inIframe = true;
    }
    const host = window.location.hostname;
    const isPreviewHost =
      host.includes("id-preview--") || host.includes("lovableproject.com");

    // Timezone — skip in preview/iframe hosts where the serverFn auth header
    // can race with session hydration and surface a spurious 401 in the
    // preview error overlay.
    if (!inIframe && !isPreviewHost) {
      try {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
        if (tz) {
          // Re-check the session right before firing so we never POST without a bearer token.
          supabase.auth.getSession().then(({ data }) => {
            if (data.session?.access_token) {
              captureTimezone({ data: { timezone: tz } }).catch(() => {});
            }
          }).catch(() => {});
        }
      } catch {}
    }

    // Service worker registration (push) + silent re-subscribe for already-granted users.
    if (!inIframe && !isPreviewHost && "serviceWorker" in navigator) {
      navigator.serviceWorker
        .register("/service-worker.js")
        .then(() => silentResubscribeIfNeeded(subscribeFn))
        .catch((e) => console.warn("[sw] registration failed", e));
    }
  }, [ready, subscribeFn]);

  // Watch for the original App writing to localStorage and mirror to Supabase
  useEffect(() => {
    if (!ready) return;
    if (devBypass) return;
    let lastSerialized = window.localStorage.getItem(USER_KEY) || "";
    let lastStageWritten: string | null = null;
    let inFlight: Promise<void> | null = null;

    // Seed lastStageWritten from the current DB row so we don't bump
    // stage_changed_at on every page-load no-op sync.
    (async () => {
      const { data } = await supabase
        .from("profiles")
        .select("life_stage")
        .eq("id", session.user.id)
        .maybeSingle();
      lastStageWritten = (data?.life_stage as string | null) ?? null;
    })();

    const sync = async () => {
      const raw = window.localStorage.getItem(USER_KEY);
      if (!raw || raw === lastSerialized) return;
      lastSerialized = raw;
      try {
        const u = JSON.parse(raw);
        const fullName =
          (u.name && String(u.name).trim()) ||
          [u.firstName, u.lastName].filter(Boolean).join(" ").trim() ||
          null;
        const healthProfile: HealthProfile = {
          hrtType: u.hrtType || "",
          hrtTypes: u.hrtTypes || [],
          hrtDose: u.hrtDose || "",
          // hrtDuration intentionally omitted — derived from hrtStartDate. Existing JSONB values are preserved by partial JSONB merge? No: this is a full JSONB overwrite, so legacy values will be dropped on next save. Acceptable per spec.
          hrtSchedule: u.hrtSchedule || "",
          hrtStartDate: (typeof u.hrtStartDate === "string" && /^\d{4}-\d{2}-\d{2}$/.test(u.hrtStartDate)) ? u.hrtStartDate : null,
          otherMeds: u.otherMeds || "",
          dob: u.dob || "",
          otherConditions: u.otherConditions || "",
          device: u.device || "",
          stage: u.stage || "",
        };
        const newStage: string | null = u.lifeStage ?? u.stage ?? null;
        const stageChanged = newStage !== lastStageWritten;
        const lastPeriod = (typeof u.lastPeriodDate === "string" && /^\d{4}-\d{2}-\d{2}$/.test(u.lastPeriodDate))
          ? u.lastPeriodDate
          : null;
        const update = {
          name: fullName,
          // SECURITY: profiles.email is NOT written from the client. The auth
          // email is the only source of truth for transactional mail, and is
          // changed only through Supabase Auth's verified email-change flow.
          life_stage: newStage,
          focus_area: u.focusArea ?? null,
          onboarding_completed: !!u.healthProfileComplete || !!fullName,
          health_profile: healthProfile as any,
          last_period_date: lastPeriod,
          ...(typeof u.wearableDevice === "string" ? { wearable_device: u.wearableDevice || null } : {}),
          // Audit trail: stamp stage_changed_at on initial set or any change.
          ...(stageChanged && newStage ? { stage_changed_at: new Date().toISOString() } : {}),
          // NOTE: is_premium is intentionally NOT written here.
          // Stripe webhook is the single source of truth for premium status.
        };
        inFlight = supabase
          .from("profiles")
          .update(update)
          .eq("id", session.user.id)
          .then(() => {
            if (stageChanged) lastStageWritten = newStage;
            inFlight = null;
            // Welcome-email trigger: now that life_stage is in the DB, kick
            // off the post-signup handler. It's idempotent (guarded by
            // profiles.welcome_email_sent_at), so it's safe to call on every
            // stage write — only the first one with a non-null stage will
            // actually send.
            if (newStage) {
              postSignupHandler({
                data: {
                  email: u.email ?? session.user.email ?? "",
                  firstName: u.firstName ?? "",
                  lastName: u.lastName ?? "",
                },
              }).catch((e) => console.warn("[post-signup] trigger failed", e));
            }
          }) as unknown as Promise<void>;
        await inFlight;
      } catch (e) {
        console.error("Failed to sync profile", e);
      }
    };

    const onMenoiqStorage = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (!detail || detail.key === USER_KEY) sync();
    };

    const onVisibility = () => {
      if (document.visibilityState === "hidden") sync();
    };

    // Best-effort flush on page unload using sendBeacon-style sync.
    // We just trigger the supabase call; the browser will keep it alive briefly.
    const onBeforeUnload = () => { sync(); };

    // Poll as a safety net (handles cases where events don't fire).
    const interval = window.setInterval(sync, 1500);
    window.addEventListener("storage", sync);
    window.addEventListener("menoiq:storage", onMenoiqStorage);
    window.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("beforeunload", onBeforeUnload);

    // Initial sync immediately in case localStorage was populated before this effect ran.
    sync();

    return () => {
      window.clearInterval(interval);
      window.removeEventListener("storage", sync);
      window.removeEventListener("menoiq:storage", onMenoiqStorage);
      window.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("beforeunload", onBeforeUnload);
    };
  }, [ready, session.user.id, session.user.email]);

  if (!ready) {
    return (
      <div
        style={{
          maxWidth: 420,
          margin: "0 auto",
          minHeight: 400,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "#6E4B71",
          fontFamily: "system-ui, sans-serif",
          fontSize: 14,
        }}
      >
        Loading your profile…
      </div>
    );
  }

  return (
    <PlanContext.Provider value={planFlags}>
      {children}
      <PushOptInPrompt />
    </PlanContext.Provider>
  );

}
